<template>
  <div ref="googleMap" class="overlay-map" :style="{ height: height }"></div>
</template>

<script>
import { getGoogleMapsLoader } from "@/mixins/GoogleMapLoaderSingleton";

/**
 * Read-only map that renders the field outline plus one or more vegetation-index
 * raster overlays positioned by their bounding box. Each overlay supports an
 * opacity and an optional left-clip (for the slider comparison).
 *
 * overlays: [{ url, bbox: [[lng,lat] x4], opacity: 0..1, clipRightPct: 0..100|null }]
 * outlineCoordinates: [{ latitude, longitude }]
 * circle: { lat, lng, radiusKm }  (outline for circular geofences)
 */
export default {
  name: "IndexOverlayMap",
  props: {
    overlays: { type: Array, default: () => [] },
    outlineCoordinates: { type: Array, default: () => [] },
    circle: { type: Object, default: null },
    height: { type: String, default: "480px" },
  },
  data() {
    return {
      google: null,
      map: null,
      OverlayClass: null,
      overlayInstances: [],
      outlineShape: null,
    };
  },
  watch: {
    overlays: {
      deep: true,
      handler() {
        this.syncOverlays();
      },
    },
    outlineCoordinates() {
      this.drawOutline();
    },
    circle: {
      deep: true,
      handler() {
        this.drawOutline();
      },
    },
  },
  async mounted() {
    try {
      const loader = getGoogleMapsLoader();
      this.google = await loader.load();
      this.map = new this.google.maps.Map(this.$refs.googleMap, {
        center: { lat: 0, lng: 0 },
        zoom: 4,
        mapTypeId: "satellite",
        tilt: 0,
        streetViewControl: false,
        fullscreenControl: false,
        mapTypeControl: false,
      });
      this.OverlayClass = this.buildOverlayClass(this.google);
      this.drawOutline();
      this.syncOverlays();
      this.fit();
      // Keep the emitted overlay rect in sync as the map pans/zooms/renders.
      this.map.addListener("bounds_changed", () => this.emitRects());
      this.map.addListener("idle", () => this.emitRects());
    } catch (e) {
      console.error("IndexOverlayMap init error:", e);
    }
  },
  beforeDestroy() {
    this.clearOverlays();
    if (this.outlineShape) this.outlineShape.setMap(null);
  },
  methods: {
    buildOverlayClass(google) {
      return class RasterOverlay extends google.maps.OverlayView {
        constructor(bounds, url, opacity, clipRightPct) {
          super();
          this.bounds_ = bounds;
          this.url_ = url;
          this.opacity_ = opacity == null ? 1 : opacity;
          this.clipRightPct_ = clipRightPct;
          this.div_ = null;
          this.img_ = null;
        }
        onAdd() {
          this.div_ = document.createElement("div");
          this.div_.style.borderStyle = "none";
          this.div_.style.position = "absolute";
          const img = document.createElement("img");
          img.src = this.url_;
          img.style.width = "100%";
          img.style.height = "100%";
          img.style.position = "absolute";
          this.applyStyle(img);
          this.img_ = img;
          this.div_.appendChild(img);
          this.getPanes().overlayLayer.appendChild(this.div_);
        }
        applyStyle(img) {
          const el = img || this.img_;
          if (!el) return;
          el.style.opacity = this.opacity_;
          el.style.clipPath =
            this.clipRightPct_ == null
              ? "none"
              : `inset(0 ${100 - this.clipRightPct_}% 0 0)`;
        }
        draw() {
          const proj = this.getProjection();
          if (!proj || !this.div_) return;
          const sw = proj.fromLatLngToDivPixel(this.bounds_.getSouthWest());
          const ne = proj.fromLatLngToDivPixel(this.bounds_.getNorthEast());
          this.div_.style.left = sw.x + "px";
          this.div_.style.top = ne.y + "px";
          this.div_.style.width = ne.x - sw.x + "px";
          this.div_.style.height = sw.y - ne.y + "px";
        }
        onRemove() {
          if (this.div_ && this.div_.parentNode) {
            this.div_.parentNode.removeChild(this.div_);
          }
          this.div_ = null;
          this.img_ = null;
        }
        setOpacity(o) {
          this.opacity_ = o;
          this.applyStyle();
        }
        setClip(pct) {
          this.clipRightPct_ = pct;
          this.applyStyle();
        }
        setImage(url) {
          this.url_ = url;
          if (this.img_) this.img_.src = url;
        }
      };
    },
    bboxToBounds(bbox) {
      if (!Array.isArray(bbox) || bbox.length < 4) return null;
      const lngs = bbox.map((c) => c[0]);
      const lats = bbox.map((c) => c[1]);
      const sw = new this.google.maps.LatLng(Math.min(...lats), Math.min(...lngs));
      const ne = new this.google.maps.LatLng(Math.max(...lats), Math.max(...lngs));
      return new this.google.maps.LatLngBounds(sw, ne);
    },
    clearOverlays() {
      this.overlayInstances.forEach((o) => o.setMap(null));
      this.overlayInstances = [];
    },
    syncOverlays() {
      if (!this.map || !this.OverlayClass) return;
      const defs = this.overlays || [];
      // Rebuild when count changes; otherwise update in place (opacity/clip/url).
      if (defs.length !== this.overlayInstances.length) {
        this.clearOverlays();
        defs.forEach((d) => {
          if (!d.url || !d.bbox) return;
          const bounds = this.bboxToBounds(d.bbox);
          if (!bounds) return;
          const ov = new this.OverlayClass(bounds, d.url, d.opacity, d.clipRightPct);
          ov.setMap(this.map);
          this.overlayInstances.push(ov);
        });
      } else {
        defs.forEach((d, i) => {
          const ov = this.overlayInstances[i];
          if (!ov) return;
          ov.setImage(d.url);
          ov.setOpacity(d.opacity == null ? 1 : d.opacity);
          ov.setClip(d.clipRightPct == null ? null : d.clipRightPct);
        });
      }
      // Overlay divs are positioned on the next map draw; emit rects shortly after.
      setTimeout(() => this.emitRects(), 0);
    },
    /**
     * Emit each overlay's on-screen rect (in map-container pixel coordinates) so
     * a parent can align UI (e.g. the comparison slider divider) exactly to the
     * raster image extent rather than the whole map.
     */
    emitRects() {
      if (!this.map || !this.$refs.googleMap) return;
      const mapRect = this.$refs.googleMap.getBoundingClientRect();
      const rects = this.overlayInstances.map((ov) => {
        const d = ov && ov.div_;
        if (!d) return null;
        const r = d.getBoundingClientRect();
        return {
          left: r.left - mapRect.left,
          top: r.top - mapRect.top,
          width: r.width,
          height: r.height,
        };
      });
      this.$emit("overlay-rects", rects);
    },
    drawOutline() {
      if (!this.map || !this.google) return;
      if (this.outlineShape) {
        this.outlineShape.setMap(null);
        this.outlineShape = null;
      }
      if (this.circle && this.circle.lat != null && this.circle.radiusKm) {
        this.outlineShape = new this.google.maps.Circle({
          center: { lat: Number(this.circle.lat), lng: Number(this.circle.lng) },
          radius: Number(this.circle.radiusKm) * 1000,
          strokeColor: "#29B6F6",
          strokeWeight: 2,
          fillOpacity: 0,
          map: this.map,
        });
      } else {
        const coords = (this.outlineCoordinates || [])
          .map((c) => ({ lat: Number(c.latitude), lng: Number(c.longitude) }))
          .filter((c) => !isNaN(c.lat) && !isNaN(c.lng));
        if (coords.length >= 3) {
          this.outlineShape = new this.google.maps.Polygon({
            paths: coords,
            strokeColor: "#29B6F6",
            strokeWeight: 2,
            fillOpacity: 0,
            map: this.map,
          });
        }
      }
      this.fit();
    },
    fit() {
      if (!this.map || !this.google) return;
      const bounds = new this.google.maps.LatLngBounds();
      let has = false;
      if (this.circle && this.circle.lat != null && this.circle.radiusKm && this.outlineShape) {
        this.map.fitBounds(this.outlineShape.getBounds());
        return;
      }
      (this.outlineCoordinates || []).forEach((c) => {
        if (c.latitude != null && c.longitude != null) {
          bounds.extend(new this.google.maps.LatLng(Number(c.latitude), Number(c.longitude)));
          has = true;
        }
      });
      if (!has && this.overlays && this.overlays[0] && this.overlays[0].bbox) {
        const b = this.bboxToBounds(this.overlays[0].bbox);
        if (b) {
          this.map.fitBounds(b);
          return;
        }
      }
      if (has) this.map.fitBounds(bounds);
    },
  },
};
</script>

<style scoped>
.overlay-map {
  width: 100%;
  border-radius: 6px;
}
</style>
